<!-- Title actions is the top of most screens, including a title on the left and actions on the right -->
<template lang="pug">
.smx-title-actions.flex.justify-between.items-center
  .title.flex
    h1.mr-4(v-if="!hasSlot('title') && !props.titleLoading") {{ props.title }}
    el-skeleton.mr-4(
      v-else-if="!hasSlot('title') && props.titleLoading",
      animated,
      :rows="0"
    )
      template(#template)
        el-skeleton-item(variant="h1", style="width: 200px")
    slot(name="title")
    //- Tag
    el-tag(v-if="props.tag !== undefined", round, aria-label="amount-tag") {{ props.tag }}
    .loading-indicator(
      v-if="props.loading !== undefined",
      v-loading="props.loading",
      class="md:ml-8"
    )
  //- On Desktop all actions are on the right hand side
  .actions.flex(v-if="mq.mdPlus")
    //- Search
    template(v-if="hasSearchSlot")
      slot(name="search")
    .search(v-else-if="showSearch") 
      el-input(
        v-model="search",
        icon="search",
        clearable,
        :placeholder="$t('search')"
      )
        template(#prefix)
          font-awesome-icon(:icon="['fas', 'fa-magnifying-glass']")
    //- Custom action slot
    slot(name="actions")
    //- Action buttons
    template(v-for="action in props.actions", :key="action.label")
      router-link(v-if="action.link", :to="action.link")
        el-button(:type="action.type") {{ action.label }}
          template(v-if="action.icon", #icon)
            font-awesome-icon(:icon="['fas', action.icon]")
      el-button(v-else, :type="action.type", @click="action.onClick") {{ action.label }}
        template(v-if="action.icon", #icon)
          font-awesome-icon(:icon="['fas', action.icon]")

  //- On mobile, search is reduced to an icon button which opens a search underneath the title 
  el-button(
    v-if="!mq.mdPlus && (hasSearchSlot || showSearch)",
    link,
    @click="switchSearchOnMobile"
  )
    font-awesome-icon(:icon="['fas', 'fa-magnifying-glass']")

.mobile-search.mt-4.w-full(v-if="!mq.mdPlus && searchVisible")
  template(v-if="hasSearchSlot")
    slot(name="search")
  .search(v-else) 
    el-input(v-model="search", icon="search")
      template(#prefix)
        font-awesome-icon(:icon="['fas', 'fa-magnifying-glass']")

//- TODO: for now we only support single actions on mobile
template(v-if="!mq.mdPlus && (props.actions?.length ?? 0 > 0)")
  router-link.fixed.bottom-8.right-8.z-50(
    v-if="props.actions[0].link",
    :to="props.actions[0].link"
  )
    el-button.justify-self-end(type="primary", size="large", circle) 
      template(v-if="props.actions[0]?.icon", #icon)
        font-awesome-icon(:icon="['fas', props.actions[0].icon]")
  el-button.fixed.bottom-8.right-8.z-50(
    v-else,
    type="primary",
    size="large",
    circle,
    @click="props.actions[0].onClick"
  ) 
    template(v-if="props.actions[0]?.icon", #icon)
      font-awesome-icon(:icon="['fas', props.actions[0].icon]")
</template>

<script setup lang="ts">
import type { RouteLocationRaw } from "vue-router";
import { computed, ref, useSlots, watch } from "vue";
import { useMq } from "vue3-mq";

const mq = useMq();

export type SmxTitleActionButton = {
  label: string;
  icon: string;
  // If set, onClick will be ignored and instead a router link is being rendered
  link?: RouteLocationRaw;
  onClick?: Function;
  type?: string;
};

const props = withDefaults(
  defineProps<{
    title?: string;
    titleLoading?: boolean;
    tag?: string | number;
    loading?: boolean;
    showSearch?: boolean;
    actions?: SmxTitleActionButton[];
  }>(),
  {
    tag: undefined,
    title: "",
    showSearch: false,
    actions: () => [],
  },
);

const emit = defineEmits(["search"]);

const slots = useSlots();

const hasSearchSlot = computed(() => {
  return slots["search"] !== undefined;
});

const search = defineModel<string>("search");

const searchVisible = ref(false);

function switchSearchOnMobile() {
  searchVisible.value = !searchVisible.value;
}

function hasSlot(name) {
  return !!slots[name];
}
</script>

<style scoped>
.actions > :not(:last-child) {
  margin-right: 1rem;
}

.smx-title-actions {
  min-height: 32px;
}
</style>
